import {
  Badge, Box, Button, Flex, Input, Text,
  Table, TableContainer, Tbody, Td, Th, Thead, Tr,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { api } from "../../../services/api";
import { UserDetail } from "../UserDetail";

const GREEN  = "#004a33";
const PAGE_SIZE = 25;

const inputProps = {
  borderRadius: "8px",
  fontSize: "13px",
  bg: "white",
  border: "1px solid rgba(0,74,51,.2)",
  _focus: { borderColor: GREEN, boxShadow: `0 0 0 1px ${GREEN}` },
};

export function UserCard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    async function fetchUsers() {
      try {
        const response = await api.get("/api/v1/users");
        setUsers(response.data || []);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase().trim();
    return users.filter(u =>
      u.full_name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.document_number?.toLowerCase().includes(q)
    );
  }, [users, search]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, currentPage]);

  const fmtDate = (val) => {
    const d = new Date(val);
    if (isNaN(d)) return "—";
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <Box>
      {/* Section header */}
      <Flex align="center" justify="space-between" mb="20px" wrap="wrap" gap="10px">
        <Box>
          <Text fontSize="11px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.08em">
            Plataforma
          </Text>
          <Text fontSize="20px" fontWeight="900" color={GREEN}>
            Usuários
            <Text as="span" fontSize="14px" fontWeight="600" color="gray.400" ml="8px">({users.length})</Text>
          </Text>
        </Box>
      </Flex>

      {/* Search */}
      <Box position="relative" maxW="360px" mb="14px">
        <Box position="absolute" left="10px" top="50%" transform="translateY(-50%)" color="gray.400" pointerEvents="none">
          <FiSearch size={14} />
        </Box>
        <Input
          pl="32px"
          placeholder="Buscar por nome, e-mail ou CPF…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          {...inputProps}
          size="sm"
        />
      </Box>
      <Text fontSize="12px" color="gray.400" mb="14px">
        {filteredUsers.length} de {users.length} usuário{users.length !== 1 ? "s" : ""}
      </Text>

      {/* Table */}
      <Box bg="white" borderRadius="16px" border="1px solid rgba(0,74,51,.08)" boxShadow="0 2px 12px rgba(0,74,51,.05)" overflow="hidden">
        {loading ? (
          <Box py="48px" textAlign="center">
            <Text fontSize="14px" color="gray.400">Carregando…</Text>
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box py="48px" textAlign="center">
            <Text fontSize="15px" fontWeight="700" color="gray.400">
              {search ? `Nenhum usuário encontrado para "${search}"` : "Nenhum usuário cadastrado"}
            </Text>
          </Box>
        ) : (
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead bg="rgba(0,74,51,.04)">
                <Tr>
                  <Th color={GREEN} fontSize="11px" py="12px">#</Th>
                  <Th color={GREEN} fontSize="11px">Nome</Th>
                  <Th color={GREEN} fontSize="11px">E-mail</Th>
                  <Th color={GREEN} fontSize="11px">Telefone</Th>
                  <Th color={GREEN} fontSize="11px">Cadastro</Th>
                  <Th color={GREEN} fontSize="11px">Tipo</Th>
                  <Th color={GREEN} fontSize="11px">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pagedUsers.map(u => (
                  <Tr key={u.id} _hover={{ bg: "rgba(0,74,51,.025)" }} transition="background 0.1s">
                    <Td py="10px">
                      <Text fontSize="12px" fontWeight="700" color={GREEN}>#{u.id}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="12px" fontWeight="600" noOfLines={1} maxW="200px">{u.full_name || "—"}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="12px" color="gray.600" noOfLines={1} maxW="220px">{u.email}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="12px" color="gray.600">{u.phone_number || "—"}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="12px" color="gray.600">{fmtDate(u.created_at)}</Text>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={u.access === "admin" ? "orange" : "gray"}
                        fontSize="9px" borderRadius="4px"
                      >
                        {u.access === "admin" ? "Admin" : "Cliente"}
                      </Badge>
                    </Td>
                    <Td>
                      <UserDetail userId={u.id} userName={u.full_name || u.email} />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
        <Flex
          align="center" justify="space-between" px="20px" py="10px"
          borderTop="1px solid rgba(0,74,51,.06)" wrap="wrap" gap="8px"
        >
          <Text fontSize="12px" color="gray.400">
            {users.length} usuário{users.length !== 1 ? "s" : ""} na plataforma
          </Text>
          {filteredUsers.length > 0 && (
            <Flex align="center" gap="10px">
              <Button
                size="xs" variant="ghost" color={GREEN} fontWeight="700" fontSize="11px"
                isDisabled={currentPage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <Text fontSize="12px" color="gray.500" fontWeight="600">
                Página {currentPage} de {totalPages}
              </Text>
              <Button
                size="xs" variant="ghost" color={GREEN} fontWeight="700" fontSize="11px"
                isDisabled={currentPage >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                Próxima
              </Button>
            </Flex>
          )}
        </Flex>
      </Box>
    </Box>
  );
}
